import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarPlus, Trash2, X, Pill, MapPin, Clock, Pencil, AlertTriangle, HandCoins, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import BidsPanel from "@/components/BidsPanel";

const PAGE_SIZE = 50;

const BLANK = {
  user_id:"", location_id:"", start_time:"", end_time:"",
  requires_medication:false, notes:"", funding_source_id:"", billable: true,
  individual_id: "", service_context: "participant",
};

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Shifts() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [locs, setLocs] = useState([]);
  const [funds, setFunds] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [editing, setEditing] = useState(null);  // shift id being edited | "new" | null
  const [validation, setValidation] = useState(null);
  const [f, setF] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [bidsShift, setBidsShift] = useState(null);

  const load = async (p = 1) => {
    const s = await api.get("/shifts", { params: { page: p, limit: PAGE_SIZE } });
    setShifts(s.data.data); setPage(s.data.page); setPages(s.data.pages); setTotal(s.data.total);
    if (user.role !== "dsp") {
      const [u, l, fs, inds] = await Promise.all([
        api.get("/users", { params: { limit: 500 } }),
        api.get("/locations"),
        api.get("/funding-sources"),
        api.get("/individuals", { params: { limit: 500 } }),
      ]);
      setUsers(u.data.data.filter(x=>x.role==="dsp")); setLocs(l.data); setFunds(fs.data);
      setIndividuals(inds.data.data || []);
    }
  };
  useEffect(() => { load(1); }, []);  // eslint-disable-line

  const openNew = () => { setF(BLANK); setEditing("new"); setValidation(null); };
  const openEdit = (s) => {
    setF({
      user_id: s.user_id || "",
      location_id: s.location_id,
      start_time: toLocalInput(s.start_time),
      end_time: toLocalInput(s.end_time),
      requires_medication: !!s.requires_medication,
      notes: s.notes || "",
      funding_source_id: s.funding_source_id || "",
      billable: s.billable !== false,
      individual_id: s.individual_id || "",
      service_context: s.service_context || "participant",
    });
    setEditing(s.id);
    setValidation(null);
  };
  const close = () => { setEditing(null); setValidation(null); };

  const validate = async () => {
    if (!f.user_id || !f.start_time || !f.end_time) { setValidation(null); return; }
    try {
      const { data } = await api.post("/shifts/validate", {
        ...f,
        start_time: new Date(f.start_time).toISOString(),
        end_time: new Date(f.end_time).toISOString(),
      });
      setValidation(data);
    } catch (e) { /* quiet */ }
  };
  useEffect(() => { if (editing) validate(); /* eslint-disable-next-line */ }, [f.user_id, f.start_time, f.end_time, f.requires_medication, editing]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...f,
        start_time: new Date(f.start_time).toISOString(),
        end_time: new Date(f.end_time).toISOString(),
        funding_source_id: f.funding_source_id || null,
        individual_id: f.individual_id || null,
        service_context: f.service_context || "participant",
      };
      if (editing === "new") {
        const r = await api.post("/shifts", body);
        toast.success(`APPROVED: Shift ${r.data.code} scheduled`);
      } else {
        const r = await api.put(`/shifts/${editing}`, body);
        toast.success(r.data.message);
      }
      close(); load(page);
    } catch (e) { toast.error("DENIED: " + formatApiError(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete shift?")) return;
    try { await api.delete(`/shifts/${id}`); load(page); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openForBids = async (e, s) => {
    e.stopPropagation();
    try {
      await api.post(`/shifts/${s.id}/open`);
      toast.success(`${s.code} posted to Open Shifts · DSPs notified`);
      load(page);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const closeBidding = async (e, s) => {
    e.stopPropagation();
    try {
      await api.post(`/shifts/${s.id}/close`);
      toast.success(`${s.code} withdrawn from Open Shifts`);
      load(page);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const pendingBids = (s) => (s.bids || []).filter((b) => b.status === "pending").length;

  const isRisky = (s) => s.requires_medication || s.status === "unassigned";

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="accp-label">Scheduling engine</div>
          <h1 className="text-4xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>
            {user.role === "dsp" ? "My schedule" : "Shifts"}
          </h1>
          <p className="text-muted-foreground mt-1">Compliance-locked: CPR + First Aid required for every applicable participant shift; medication shifts additionally require CMT + RN Delegation. All required certifications must remain valid through the entire shift.</p>
        </div>
        {user.role==="admin" && (
          <button className="accp-btn-primary" onClick={openNew} data-testid="add-shift-btn">
            <CalendarPlus size={16}/> Schedule shift
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {shifts.map((s) => (
          <div
            key={s.id}
            className={`accp-card p-5 cursor-pointer transition ${
              isRisky(s) ? "ring-1 ring-[#B71C1C]/30 hover:ring-[#B71C1C]/60" : "hover:ring-1 hover:ring-primary/30"
            }`}
            onClick={() => user.role === "admin" && openEdit(s)}
            data-testid={`shift-${s.code || s.id}`}
            title={isRisky(s) ? "High-risk: medication shift or unassigned" : ""}
          >
            <div className="flex justify-between items-start">
              <div>
                <div className="accp-label font-mono">{s.code} · {s.status}</div>
                <h3 className="text-lg font-semibold text-primary mt-1" style={{fontFamily:'Outfit'}}>
                  {s.staff_name || <span className="text-destructive">Unassigned</span>}
                  {s.staff_code && <span className="ml-2 text-xs font-mono text-muted-foreground">{s.staff_code}</span>}
                </h3>
              </div>
              <div className="flex gap-1 flex-wrap">
                {s.requires_medication && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full compliance-expired text-[11px] font-semibold">
                    <Pill size={11}/> MEDS
                  </span>
                )}
                {isRisky(s) && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FFF8E6] border border-[#FFE4A0] text-[#7A5A00] text-[11px] font-semibold" title="Medication or unassigned — elevated risk">
                    <AlertTriangle size={11}/> RISK
                  </span>
                )}
                {s.open_for_bids && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#DCF5D9] text-[#4A7C1E] text-[11px] font-semibold">
                    <HandCoins size={11}/> OPEN
                  </span>
                )}
                {s.auto_filled && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#D9EFE2] text-[#2E7D5A] text-[11px] font-semibold"
                    data-testid={`auto-filled-${s.code}`}
                    title="Auto-assigned to the sole qualified bidder after 12h"
                  >
                    <HandCoins size={11}/> AUTO
                  </span>
                )}
                {pendingBids(s) > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setBidsShift(s); }}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#5B3AA4] text-white text-[11px] font-semibold hover:opacity-90"
                    data-testid={`bids-badge-${s.code}`}
                    title="Review bids"
                  >
                    <UsersIcon size={11}/> {pendingBids(s)} bid{pendingBids(s) === 1 ? "" : "s"}
                  </button>
                )}
              </div>
            </div>
            <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Clock size={13}/> {new Date(s.start_time).toLocaleString()} → {new Date(s.end_time).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</div>
              <div className="flex items-center gap-2"><MapPin size={13}/> {s.location?.name}</div>
              {s.individual_name && (
                <div className="flex items-center gap-2" data-testid={`shift-participant-${s.code}`}>
                  <UsersIcon size={13}/>
                  <span className="font-semibold text-primary">{s.individual_name}</span>
                  <span className="text-[11px] font-mono">{s.individual_code}</span>
                </div>
              )}
              {!s.individual_name && s.service_context && s.service_context !== "participant" && (
                <div className="text-[11px] uppercase tracking-wide inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200 w-fit">
                  {s.service_context}
                </div>
              )}
              {s.funding_source_code && (
                <div className="text-xs font-mono">
                  {s.funding_source_code} · {s.service_code} · GL {s.gl_labor_account}/{s.gl_revenue_account}
                </div>
              )}
              {s.notes && <div className="italic text-xs">&ldquo;{s.notes}&rdquo;</div>}
            </div>
            {user.role==="admin" && (
              <div className="mt-3 flex gap-2 flex-wrap" onClick={(e)=>e.stopPropagation()}>
                <button className="accp-btn-ghost text-xs" onClick={()=>openEdit(s)} data-testid={`edit-shift-${s.code}`}>
                  <Pencil size={13}/> Edit
                </button>
                {s.status === "unassigned" && !s.open_for_bids && (
                  <button
                    className="accp-btn-ghost text-xs text-[#4A7C1E]"
                    onClick={(e)=>openForBids(e, s)}
                    data-testid={`open-bids-${s.code}`}
                  >
                    <HandCoins size={13}/> Open for bids
                  </button>
                )}
                {s.open_for_bids && (
                  <>
                    <button
                      className="accp-btn-ghost text-xs"
                      onClick={(e)=>{ e.stopPropagation(); setBidsShift(s); }}
                      data-testid={`review-bids-${s.code}`}
                    >
                      <UsersIcon size={13}/> Review bids ({(s.bids||[]).length})
                    </button>
                    <button
                      className="accp-btn-ghost text-xs text-muted-foreground"
                      onClick={(e)=>closeBidding(e, s)}
                      data-testid={`close-bids-${s.code}`}
                    >
                      Close bidding
                    </button>
                  </>
                )}
                <button className="accp-btn-ghost text-xs text-destructive" onClick={()=>del(s.id)}>
                  <Trash2 size={13}/> Remove
                </button>
              </div>
            )}
          </div>
        ))}
        {shifts.length===0 && <div className="text-muted-foreground col-span-full">No shifts yet.</div>}
      </div>

      {pages > 1 && (
        <div className="accp-card overflow-hidden">
          <Pagination page={page} pages={pages} total={total} onChange={load} testId="shifts-pagination" />
        </div>
      )}

      {editing && (
        <div className="accp-modal-shell" onClick={close}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="accp-modal-card accp-modal-card-lg space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>
                {editing === "new" ? "Schedule shift" : "Edit shift"}
              </h3>
              <button type="button" onClick={close}><X size={18}/></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2"><span className="accp-label">DSP</span>
                <select required className="accp-input mt-1" value={f.user_id} onChange={(e)=>setF({...f,user_id:e.target.value})}>
                  <option value="">Select…</option>
                  {users.map((u)=><option key={u.id} value={u.id}>{u.code} — {u.name}</option>)}
                </select>
              </label>
              <label className="col-span-2"><span className="accp-label">Site</span>
                <select required className="accp-input mt-1" value={f.location_id} onChange={(e)=>setF({...f,location_id:e.target.value})}>
                  <option value="">Select…</option>
                  {locs.map((l)=><option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
                </select>
              </label>
              <label className="col-span-2"><span className="accp-label">Service context</span>
                <select className="accp-input mt-1" value={f.service_context}
                  onChange={(e)=>setF({...f, service_context:e.target.value, individual_id: e.target.value==="participant"? f.individual_id : ""})}
                  data-testid="shift-service-context">
                  <option value="participant">Participant service</option>
                  <option value="training">Training</option>
                  <option value="supervision">Supervision</option>
                  <option value="staff_meeting">Staff meeting</option>
                  <option value="admin">Administrative</option>
                  <option value="other">Other (non-participant)</option>
                </select>
              </label>
              {f.service_context === "participant" && (
                <label className="col-span-2"><span className="accp-label">Participant (Individual) *</span>
                  <select required className="accp-input mt-1" value={f.individual_id}
                    onChange={(e)=>setF({...f,individual_id:e.target.value})}
                    data-testid="shift-individual-picker">
                    <option value="">Select participant…</option>
                    {individuals.filter(i => i.status === "active" || i.status === "intake")
                      .map((i)=><option key={i.id} value={i.id}>{i.code} — {i.last_name}, {i.first_name}</option>)}
                  </select>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Clock-in is not documentation. Selecting the participant here confirms who the DSP is serving.
                  </div>
                </label>
              )}
              <label><span className="accp-label">Start</span>
                <input type="datetime-local" required className="accp-input mt-1" value={f.start_time} onChange={(e)=>setF({...f,start_time:e.target.value})}/>
              </label>
              <label><span className="accp-label">End</span>
                <input type="datetime-local" required className="accp-input mt-1" value={f.end_time} onChange={(e)=>setF({...f,end_time:e.target.value})}/>
              </label>
              <label className="col-span-2"><span className="accp-label">Funding source (waiver)</span>
                <select className="accp-input mt-1" value={f.funding_source_id} onChange={(e)=>setF({...f,funding_source_id:e.target.value})}>
                  <option value="">— none (non-billable) —</option>
                  {funds.map((x)=><option key={x.id} value={x.id}>{x.code} — {x.name} (${x.billing_rate_per_hour}/hr · {x.units_per_hour} units/hr)</option>)}
                </select>
              </label>
              <label className="col-span-2 flex items-center gap-2 mt-1">
                <input type="checkbox" checked={f.requires_medication} onChange={(e)=>setF({...f,requires_medication:e.target.checked})}/>
                <span className="text-sm">Medication shift — requires RN Delegation</span>
              </label>
              <label className="col-span-2"><span className="accp-label">Notes</span>
                <input className="accp-input mt-1" value={f.notes} onChange={(e)=>setF({...f,notes:e.target.value})}/>
              </label>
            </div>

            {validation && (
              <div className={`rounded-xl p-3 text-sm ${validation.compliant ? "compliance-valid" : "compliance-expired"}`} data-testid="compliance-preview">
                {validation.compliant
                  ? <>APPROVED: All compliance checks passed (cert, expiration-during-shift, overlap).</>
                  : <>DENIED — Staff cannot be scheduled:
                      <ul className="list-disc pl-5 mt-1">{validation.problems.map((p)=><li key={p}>{p}</li>)}</ul>
                    </>}
              </div>
            )}

            <button type="submit" className="accp-btn-primary w-full"
                    disabled={saving || (validation && !validation.compliant)}
                    data-testid="shift-submit">
              {saving ? "Saving…" : (editing === "new" ? "Save shift" : "Save changes")}
            </button>
          </form>
        </div>
      )}

      {bidsShift && (
        <BidsPanel
          shift={bidsShift}
          onClose={() => setBidsShift(null)}
          onChange={() => load(page)}
        />
      )}
    </div>
  );
}
